import {
  createFileRoute,
  redirect,
  useRouteContext,
  useSearch,
} from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { type FragmentType, graphql } from "@repo/graphql-types";
import { z } from "zod";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL, fetchQuery } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  RefundDetails,
  type RefundDetailsQueryFragment,
} from "@/components/payment/refund/refund-details";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const PatientPaymentRefundPageQuery = graphql(/* GraphQL */ `
  query PatientPaymentRefundPage($onlinePaymentGuid: String, $codAgendamento: Int, $codUsuario: Int!, $codClinica: Int) {
    ExpensumPaymentDetails(arg1: {onlinePaymentGuid: $onlinePaymentGuid, codAgendamento: $codAgendamento}) {
      status
    }
    ...RefundDetailsQueryFragment
  }
`);

const PatientPaymentRefundPageSearchSchema = z.object({
  guid: z.string().optional(),
  appointmentId: z.number().optional(),
  action: z.string().optional(),
});

const PatientPaymentRefundPage = (): JSX.Element => {
  const { guid, appointmentId } = useSearch({
    from: "/settings/payments/patient-payments/refund",
  });

  const { user } = useRouteContext({ from: "__root__" });

  const queryResult = useGraphQL(PatientPaymentRefundPageQuery, {
    onlinePaymentGuid: guid,
    codAgendamento: appointmentId,
    codUsuario: user.codUsuario,
    codClinica: user.codClinica,
  });

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton data-testid="back-button" icon={Left} align="start" />
        <HeaderTitle key="title" title="Estorno" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <RefundDetails
              data={data as FragmentType<typeof RefundDetailsQueryFragment>}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/patient-payments/refund")({
  component: PatientPaymentRefundPage,
  validateSearch: PatientPaymentRefundPageSearchSchema,
  beforeLoad: async ({ context, search }) => {
    const variables = {
      onlinePaymentGuid: search.guid,
      codAgendamento: search.appointmentId,
      codUsuario: context.user.codUsuario,
      codClinica: context.user.codClinica,
    };

    const data = await fetchQuery(context, PatientPaymentRefundPageQuery, variables);

    const status = data.ExpensumPaymentDetails?.status;

    if (status !== "paid") {
      redirect({ to: "/settings/payments/patient-payments", throw: true });
    }
  },
});
